import React, { useState, useMemo, useEffect, useRef } from "react";

/**
 * CASDM Explorer — Cloudelligent Agentic Software Delivery Model
 * ---------------------------------------------------------------
 * Drop-in React component for DeliverPro.
 *
 * Dependencies: React 17+ and Tailwind CSS. Nothing else.
 * Icons are inline SVG so there is no icon-library install.
 *
 * Usage:
 *   import CASDMExplorer from "./CASDMExplorer";
 *   <CASDMExplorer />
 *
 * Optional props:
 *   defaultPhase   number   phase index to open on mount (0-4). Default 0.
 *   onStepSelect   func     called with the step object when a card is opened.
 *   onGateSelect   func     called with the gate object when a gate is opened.
 */

/* ------------------------------------------------------------------ */
/*  BRAND TOKENS — Cloudelligent                                       */
/* ------------------------------------------------------------------ */
const C = {
  ink: "#27164F", // deep purple, dominant
  inkSoft: "#3D2A6B",
  ember: "#D74018", // orange-red, the accent
  sky: "#0089DD", // blue — agent work
  amber: "#FF9B00", // amber — human review
  moss: "#33A94F", // green — gates, exits
  mist: "#F4F3F7", // surface tint
};

/* ------------------------------------------------------------------ */
/*  STEP KINDS                                                         */
/* ------------------------------------------------------------------ */
const KIND = {
  agent: { label: "AI agent", color: C.sky, glyph: "agent" },
  human: { label: "Human review", color: C.amber, glyph: "human" },
  deliverable: { label: "Deliverable", color: C.ember, glyph: "deliverable" },
  meeting: { label: "Meeting", color: C.ink, glyph: "meeting" },
  task: { label: "Task", color: "#6B6580", glyph: "task" },
};

/* ------------------------------------------------------------------ */
/*  ROLES (swimlanes)                                                  */
/* ------------------------------------------------------------------ */
const ROLES = [
  { id: "ae", name: "C-Suite / Account Executive", short: "AE" },
  { id: "sa", name: "Solutions Architect", short: "SA" },
  { id: "pm", name: "Project Manager", short: "PM" },
  { id: "il", name: "Implementation Lead", short: "IL" },
];

/* ------------------------------------------------------------------ */
/*  AGENT ROSTER                                                       */
/* ------------------------------------------------------------------ */
const AGENTS = [
  {
    id: "product-analyst",
    name: "product-analyst",
    role: "Turns raw discovery into a written spec",
    outputs: ["Preliminary SRS", "Discovery agenda + questions", "Working SRS"],
    phases: [0, 1],
  },
  {
    id: "aws-architect",
    name: "aws-architect",
    role: "Designs and validates the AWS solution",
    outputs: [
      "Solution architecture design",
      "Feature list / per-feature architecture",
      "Unified data model",
      "TCO",
      "Runbooks & documentation",
    ],
    phases: [0, 1, 2, 4],
  },
  {
    id: "technical-pm",
    name: "technical-pm",
    role: "Converts the spec into a plan the team can run",
    outputs: ["Jira stories & sprint plan", "Baseline Jira backlog"],
    phases: [0, 2],
  },
  {
    id: "plan-reviewer",
    name: "plan-reviewer",
    role: "Checks artifacts and spec files before build starts",
    outputs: ["Artifact review", "Spec file validation"],
    phases: [2, 3],
  },
  {
    id: "security-reviewer",
    name: "security-reviewer",
    role: "Applies security, compliance and Well-Architected checks",
    outputs: ["Security gate result"],
    phases: [2],
  },
  {
    id: "orchestrator",
    name: "orchestrator",
    role: "Picks up Jira stories and routes them to build agents",
    outputs: ["Story pickup"],
    phases: [2],
  },
  {
    id: "executioner",
    name: "executioner*",
    role: "Writes the spec file per story, then writes the code",
    outputs: ["Per-story spec file", "Generated code"],
    phases: [2, 3],
  },
  {
    id: "code-reviewer",
    name: "code-reviewer",
    role: "Validates generated code before a human reads it",
    outputs: ["Code validation"],
    phases: [3],
  },
  {
    id: "qa-agent",
    name: "qa-agent",
    role: "Builds the UAT plan and reports on test outcomes",
    outputs: ["UAT plan", "UAT report", "UAT report validation"],
    phases: [3],
  },
];

/* ------------------------------------------------------------------ */
/*  THE MODEL                                                          */
/* ------------------------------------------------------------------ */
const PHASES = [
  {
    num: 0,
    name: "Internal Preparation",
    tagline: "Get the house in order before the customer sees anything",
    summary:
      "The signed SOW becomes a working project. DeliverPro provisions the tooling, the knowledge base is seeded, and the team generates the first artifacts so the kickoff is not the first time anyone has read the scope.",
    trigger: "SOW signed",
    lanes: {
      ae: [
        { t: "SOW signed", k: "task" },
        { t: "Confirm project details in DeliverPro", k: "task" },
        { t: "Assign resources / team via DeliverPro", k: "task" },
        {
          t: "DeliverPro provisions Slack, Jira, SharePoint, GitHub & Clockify",
          k: "task",
        },
        {
          t: "SOW, meeting notes & discovery docs populated in Kiro Knowledge Base",
          k: "task",
        },
        {
          t: "Sales to Delivery handoff meeting",
          k: "meeting",
          d: "AE + PM + SA + Implementation Lead. Aligns the wider team on scope, customer context, and engineering baselines.",
        },
        { t: "Discuss additional revenue opportunities", k: "task" },
      ],
      sa: [
        { t: "Generate preliminary SRS", k: "agent", a: "product-analyst" },
        {
          t: "Generate discovery meeting agenda + questions",
          k: "deliverable",
          a: "product-analyst",
        },
        { t: "Validate artifacts", k: "agent", a: "aws-architect" },
        { t: "Human review of artifacts", k: "human" },
        { t: "Commit to Git", k: "task" },
        { t: "Review technical solution with delivery team", k: "task" },
      ],
      pm: [
        { t: "Set up recurring standups (internal)", k: "task" },
        {
          t: "Internal readiness meeting (PM + SA)",
          k: "meeting",
          d: "Transcript analysis feeds the knowledge base.",
        },
        {
          t: "High-level project plan + Gantt chart + RACI",
          k: "agent",
          a: "technical-pm",
        },
        { t: "Baseline Jira backlog", k: "agent", a: "technical-pm" },
        { t: "Kickoff deck content / slides", k: "deliverable" },
        { t: "Validate artifacts", k: "agent", a: "aws-architect" },
        { t: "Human review of artifacts", k: "human" },
        { t: "Complete SOW handoff checklist", k: "task" },
        { t: "Finalize kickoff deck", k: "deliverable" },
        { t: "Add all project docs in SharePoint", k: "task" },
        {
          t: "Kickoff call (customer)",
          k: "meeting",
          d: "Transcript analysis captured.",
        },
        { t: "Weekly status calls (customer)", k: "meeting" },
      ],
      il: [],
    },
    gate: {
      id: "GATE 0",
      title: "Ready to face the customer",
      criteria: [
        "Artifacts reviewed by SA + team",
        "SOW handoff checklist complete",
        "Kickoff scheduled",
      ],
      onFail: "Fix gaps in the kickoff deck, then re-check.",
    },
  },
  {
    num: 1,
    name: "Discover & Align",
    tagline: "Turn conversations into a signed-off SRS",
    summary:
      "Recorded discovery sessions feed the knowledge base. product-analyst drafts the working SRS, aws-architect validates it, a human reviews it, and the customer signs off on scope before any design work starts.",
    trigger: "Kickoff complete",
    lanes: {
      ae: [{ t: "Attend executive check-in call 1", k: "meeting" }],
      sa: [
        {
          t: "Discovery session 1 (recorded)",
          k: "meeting",
          d: "Recording and transcript are reindexed into the knowledge base.",
        },
        { t: "Discovery sessions 2…X", k: "meeting" },
        { t: "Reindex knowledge base", k: "task" },
        {
          t: "Generate working SRS",
          k: "deliverable",
          a: "product-analyst",
          d: "Can also be executed by the Implementation Lead.",
        },
        {
          t: "Validate SRS",
          k: "agent",
          a: "aws-architect",
          d: "Can trigger a change order.",
        },
        { t: "Human review of SRS", k: "human" },
        { t: "Conduct impact analysis", k: "task" },
      ],
      pm: [
        { t: "Establish and schedule discovery sessions", k: "task" },
        {
          t: "Internal prep meeting (PM + SA + Lead)",
          k: "meeting",
        },
        {
          t: "Review SRS with internal team to align on requirements",
          k: "meeting",
        },
        {
          t: "Discovery readout of SRS session (client)",
          k: "meeting",
          d: "Transcript analysis captured. Client feedback loops back into the SRS.",
        },
        { t: "Gather final feedback & obtain customer sign-off", k: "task" },
        { t: "Add all project docs in SharePoint", k: "task" },
        { t: "Executive check-in call 1 (client)", k: "meeting" },
      ],
      il: [
        { t: "Input validated SRS", k: "task" },
        { t: "Reindex knowledge base", k: "task" },
        { t: "Generate design inputs", k: "agent", a: "aws-architect" },
      ],
    },
    gate: {
      id: "GATE 1",
      title: "Scope is agreed and written down",
      criteria: [
        "SRS reviewed by SA + team",
        "Scope and success criteria confirmed by customer",
        "Current state documented",
      ],
      onFail: "Fix gaps in Phase 1, then re-check.",
    },
  },
  {
    num: 2,
    name: "Design & Review",
    tagline: "Design it, prove it is safe, break it into stories",
    summary:
      "The architecture, data model and TCO are generated and reviewed. security-reviewer applies a security gate. Once the Implementation Plan Review passes, technical-pm turns the design into a sprint plan the delivery team can execute.",
    trigger: "GATE 1 passed",
    lanes: {
      ae: [{ t: "Attend Implementation Plan Review", k: "meeting" }],
      sa: [
        { t: "Technically validate design docs with detailed spec strategy", k: "human" },
        {
          t: "Spot gaps, out-of-scope needs, modernization opportunities, or adjacent workloads",
          k: "task",
        },
      ],
      pm: [
        {
          t: "Implementation Plan Review (IPR)",
          k: "meeting",
          d: "Presented by the Implementation Lead. Transcript analysis captured.",
        },
        {
          t: "Review design docs with project team to align on technical solution",
          k: "meeting",
        },
        {
          t: "Client meeting: share design deliverables & obtain sign-off",
          k: "meeting",
        },
        { t: "Validate Jira stories / sprint plan", k: "human" },
        { t: "Sprint planning session with the delivery team", k: "meeting" },
        { t: "Add all project docs in SharePoint", k: "task" },
        {
          t: "Reporting for escalation board, portfolio and WSR",
          k: "deliverable",
          d: "As per requirement. Includes resource budget plan update.",
        },
      ],
      il: [
        { t: "Solution architecture design", k: "deliverable", a: "aws-architect" },
        {
          t: "Feature list / per-feature architecture & domain decomposition",
          k: "deliverable",
          a: "aws-architect",
        },
        { t: "Unified data model", k: "deliverable", a: "aws-architect" },
        { t: "TCO update", k: "deliverable", a: "aws-architect" },
        { t: "Review artifacts", k: "agent", a: "plan-reviewer" },
        {
          t: "Security gate — security, compliance & WAF checks",
          k: "agent",
          a: "security-reviewer",
          d: "Can trigger a change order.",
        },
        { t: "Enrich current SRS with design and requirement changes", k: "task" },
        { t: "Conduct impact analysis on new client feedback", k: "task" },
        { t: "Reindex knowledge base", k: "task" },
        {
          t: "Generate Jira stories / sprint plan from validated SRS + design docs",
          k: "agent",
          a: "technical-pm",
        },
        { t: "Review artifact", k: "agent", a: "plan-reviewer" },
        { t: "Pick up a Jira story", k: "agent", a: "orchestrator" },
        {
          t: "Generate spec per story ID",
          k: "agent",
          a: "executioner",
        },
      ],
    },
    gate: {
      id: "GATE 2",
      title: "Design approved and broken into work",
      criteria: [
        "Solution design artifacts reviewed by SA + team",
        "Solution approved by IPR",
        "Jira stories approved by PM + SA",
      ],
      onFail: "Fix gaps in Phase 2, then re-check.",
    },
  },
  {
    num: 3,
    name: "Build & Implement",
    tagline: "Story in, reviewed code out — one work unit at a time",
    summary:
      "The build loop runs per story: spec file, code, agent validation, human review, deploy, test. UAT is planned and reported by qa-agent. Nothing leaves the loop without both an agent check and a human check.",
    trigger: "GATE 2 passed",
    lanes: {
      ae: [{ t: "Account planning session (led by AE)", k: "meeting" }],
      sa: [
        { t: "Maintain follow-on opportunity log", k: "task" },
        { t: "Commit to Git", k: "task" },
        {
          t: "Validate performance, security, and compliance criteria",
          k: "human",
          d: "Adds comments back onto the work unit.",
        },
        { t: "Provide feedback and share follow-on opp log with AEs", k: "task" },
      ],
      pm: [
        {
          t: "Weekly status call with client (status & demo)",
          k: "meeting",
        },
        {
          t: "Spot gaps, out-of-scope needs, modernization opportunities, or adjacent workloads",
          k: "task",
        },
        { t: "Add all project docs in SharePoint", k: "task" },
        {
          t: "UAT review with client",
          k: "meeting",
          d: "Transcript analysis captured.",
        },
      ],
      il: [
        {
          t: "Validate spec file",
          k: "agent",
          a: "plan-reviewer",
          d: "Can trigger a change order.",
        },
        { t: "Human review of spec file", k: "human" },
        { t: "Generate code", k: "agent", a: "executioner" },
        { t: "Conduct impact analysis", k: "task" },
        { t: "Validate code", k: "agent", a: "code-reviewer" },
        { t: "Human review of code", k: "human" },
        { t: "Reindex knowledge base", k: "task" },
        { t: "Deploy code to environment", k: "human" },
        { t: "Test code across frontend and backend (QA)", k: "task" },
        {
          t: "Develop UAT (User Acceptance Testing) plan",
          k: "agent",
          a: "qa-agent",
        },
        { t: "Generate UAT report", k: "deliverable", a: "qa-agent" },
        { t: "Validate UAT report", k: "agent", a: "qa-agent" },
        { t: "Human review of UAT report", k: "human" },
      ],
    },
    gate: {
      id: "GATE 3",
      title: "The work unit is genuinely done",
      criteria: [
        "Artifacts & deployment reviewed by SA + Lead",
        "Work unit complete",
        "Demo accepted by SA",
        "No uncontrolled scope drift",
        "Follow-on opportunities logged for AE",
      ],
      onFail: "Fix gaps in Phase 3, then re-check.",
    },
  },
  {
    num: 4,
    name: "Launch & Enable",
    tagline: "Hand it over so the customer can run it without you",
    summary:
      "Runbooks and documentation are generated and validated, knowledge transfer sessions are run with the customer, sign-off is collected, and the project is closed with a retrospective that feeds the next engagement.",
    trigger: "GATE 3 passed",
    lanes: {
      ae: [{ t: "Attend executive check-in call 2", k: "meeting" }],
      sa: [{ t: "Validate customer documentation", k: "human" }],
      pm: [
        { t: "UAT client feedback (SA support)", k: "task" },
        { t: "Request signoff document from Business Operations", k: "task" },
        { t: "Share signoff document with customer", k: "deliverable" },
        { t: "Create project closure deck", k: "task" },
        {
          t: "Project closure meeting / email (scope based)",
          k: "meeting",
          d: "Transcript analysis captured.",
        },
        { t: "Add all project docs in SharePoint", k: "task" },
        {
          t: "Project retrospective (internal)",
          k: "meeting",
        },
        {
          t: "Executive check-in call 2 (client)",
          k: "meeting",
          d: "Transcript analysis captured.",
        },
        { t: "Close project", k: "task" },
      ],
      il: [
        {
          t: "Generate runbooks / documentation",
          k: "deliverable",
          a: "aws-architect",
        },
        { t: "Validate runbooks / docs", k: "agent", a: "aws-architect" },
        { t: "Conduct KT sessions with customer", k: "meeting" },
      ],
    },
    gate: {
      id: "GATE 4",
      title: "The customer owns it now",
      criteria: [
        "UAT + signoff complete",
        "KT + runbooks delivered",
      ],
      onFail: "Fix gaps in Phase 4, then re-check.",
    },
  },
];

/* ------------------------------------------------------------------ */
/*  INLINE ICONS                                                       */
/* ------------------------------------------------------------------ */
function Glyph({ name, size = 14, color = "currentColor" }) {
  const p = {
    width: size,
    height: size,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: color,
    strokeWidth: 2,
    strokeLinecap: "round",
    strokeLinejoin: "round",
    "aria-hidden": true,
  };
  switch (name) {
    case "agent":
      return (
        <svg {...p}>
          <rect x="4" y="7" width="16" height="12" rx="3" />
          <path d="M12 3v4M9 13h.01M15 13h.01" />
        </svg>
      );
    case "human":
      return (
        <svg {...p}>
          <circle cx="12" cy="8" r="3.5" />
          <path d="M5 20c0-3.3 3.1-6 7-6s7 2.7 7 6" />
        </svg>
      );
    case "deliverable":
      return (
        <svg {...p}>
          <path d="M12 16V4M8 8l4-4 4 4" />
          <path d="M4 16v3a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-3" />
        </svg>
      );
    case "meeting":
      return (
        <svg {...p}>
          <rect x="3" y="5" width="18" height="16" rx="2" />
          <path d="M3 10h18M8 3v4M16 3v4" />
        </svg>
      );
    case "task":
      return (
        <svg {...p}>
          <circle cx="12" cy="12" r="8" />
          <path d="M9 12l2 2 4-4" />
        </svg>
      );
    case "gate":
      return (
        <svg {...p}>
          <path d="M12 3l9 9-9 9-9-9z" />
        </svg>
      );
    case "chevron":
      return (
        <svg {...p}>
          <path d="M9 6l6 6-6 6" />
        </svg>
      );
    case "close":
      return (
        <svg {...p}>
          <path d="M18 6L6 18M6 6l12 12" />
        </svg>
      );
    case "search":
      return (
        <svg {...p}>
          <circle cx="11" cy="11" r="7" />
          <path d="M20 20l-3.5-3.5" />
        </svg>
      );
    default:
      return null;
  }
}

/* ------------------------------------------------------------------ */
/*  SMALL PIECES                                                       */
/* ------------------------------------------------------------------ */

function KindChip({ kind, active, count, onClick }) {
  const k = KIND[kind];
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className="group inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
      style={{
        borderColor: active ? k.color : "#DDD9E6",
        background: active ? k.color : "#FFFFFF",
        color: active ? "#FFFFFF" : C.inkSoft,
      }}
    >
      <Glyph name={k.glyph} size={13} color={active ? "#FFFFFF" : k.color} />
      {k.label}
      {typeof count === "number" && (
        <span
          className="ml-0.5 rounded-full px-1.5 py-px text-[10px] font-semibold tabular-nums"
          style={{
            background: active ? "rgba(255,255,255,.24)" : C.mist,
            color: active ? "#FFFFFF" : C.inkSoft,
          }}
        >
          {count}
        </span>
      )}
    </button>
  );
}

function StepCard({ step, onOpen }) {
  const k = KIND[step.k];
  return (
    <button
      type="button"
      onClick={() => onOpen(step)}
      className="group relative flex w-full items-start gap-2.5 rounded-lg border bg-white p-3 text-left transition-all hover:-translate-y-px hover:shadow-md focus:outline-none focus-visible:ring-2"
      style={{ borderColor: "#E6E2EE" }}
    >
      <span
        className="mt-px flex h-6 w-6 shrink-0 items-center justify-center rounded-md"
        style={{ background: `${k.color}1A` }}
      >
        <Glyph name={k.glyph} size={13} color={k.color} />
      </span>
      <span className="min-w-0 flex-1">
        <span
          className="block text-[13px] font-medium leading-snug"
          style={{ color: C.ink }}
        >
          {step.t}
        </span>
        {step.a && (
          <span
            className="mt-1 inline-block rounded px-1.5 py-px font-mono text-[10px]"
            style={{ background: `${C.sky}14`, color: C.sky }}
          >
            {step.a}
          </span>
        )}
      </span>
      {(step.d || step.a) && (
        <span
          className="mt-1 shrink-0 opacity-0 transition-opacity group-hover:opacity-100"
          aria-hidden
        >
          <Glyph name="chevron" size={13} color="#B4AEC4" />
        </span>
      )}
    </button>
  );
}

/* ------------------------------------------------------------------ */
/*  GATE RAIL — the signature element                                  */
/* ------------------------------------------------------------------ */
function GateRail({ phases, active, onPhase, onGate }) {
  return (
    // `overflow-visible`, NOT `overflow-x-auto`.
    //
    // Per CSS overflow, setting one axis to a non-`visible` value computes the UNSET axis to
    // `auto` as well — so `overflow-x-auto` alone silently made this a scroll container on BOTH
    // axes. The gate diamonds are 28px squares rotated 45° (diagonal ≈ 39.6px) inside 36px-wide
    // boxes, and `hover:scale-110` takes that to ≈ 43.5px, so they overflowed their own box and
    // the rail grew scrollbars on both axes over content that was never meant to scroll.
    //
    // Nothing is lost by dropping the horizontal scroll: `PHASES` is a hardcoded 5-element
    // constant, the phase buttons are `flex-1 min-w-0` with truncated labels, and the diamonds are
    // a fixed 5 × 36px — the rail has no unbounded-growth path. The parent card carries `py-3` so
    // the overflowing diamonds have room to paint at rest and at hover scale instead of clipping.
    <div className="relative w-full overflow-visible py-1">
      <div className="flex min-w-0 w-full items-stretch">
        {phases.map((p, i) => {
          const isActive = i === active;
          return (
            <React.Fragment key={p.num}>
              <button
                type="button"
                onClick={() => onPhase(i)}
                aria-current={isActive ? "step" : undefined}
                className="group relative flex-1 min-w-0 px-2 py-3 text-left transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-inset"
              >
                <span
                  className="mb-2 block h-1.5 w-full rounded-full transition-all"
                  style={{
                    background: isActive ? C.ember : "#E0DBEC",
                    transform: isActive ? "scaleY(1.6)" : "scaleY(1)",
                  }}
                />
                <span
                  className="block font-mono text-[10px] font-semibold tracking-widest"
                  style={{ color: isActive ? C.ember : "#9A93AD" }}
                >
                  P{p.num}
                </span>
                <span
                  className="mt-0.5 block text-[11px] sm:text-[13px] font-semibold leading-tight truncate"
                  style={{ color: isActive ? C.ink : "#6B6580" }}
                >
                  {p.name}
                </span>
              </button>

              {/* Gate diamond */}
              <button
                type="button"
                onClick={() => onGate(p)}
                title={`${p.gate.id} — ${p.gate.title}`}
                className="relative flex w-9 shrink-0 items-center justify-center focus:outline-none focus-visible:ring-2"
              >
                <span
                  className="flex h-7 w-7 rotate-45 items-center justify-center rounded-[6px] border-2 bg-white transition-transform hover:scale-110"
                  style={{ borderColor: i <= active ? C.moss : "#D7D2E2" }}
                >
                  <span
                    className="-rotate-45 font-mono text-[10px] font-bold"
                    style={{ color: i <= active ? C.moss : "#B4AEC4" }}
                  >
                    {p.num}
                  </span>
                </span>
              </button>
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  SLIDE-OVER PANEL                                                   */
/* ------------------------------------------------------------------ */
function Panel({ open, title, eyebrow, onClose, children, accent = C.ink }) {
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    ref.current?.focus();
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div
        className="absolute inset-0 bg-black/25"
        onClick={onClose}
        aria-hidden
      />
      <div
        ref={ref}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className="relative flex h-full w-full max-w-md flex-col bg-white shadow-2xl outline-none"
      >
        <div
          className="flex items-start gap-3 border-b p-5"
          style={{ borderColor: "#EDEAF3" }}
        >
          <div className="min-w-0 flex-1">
            {eyebrow && (
              <div
                className="mb-1 font-mono text-[10px] font-semibold tracking-widest"
                style={{ color: accent }}
              >
                {eyebrow}
              </div>
            )}
            <h3
              className="text-lg font-semibold leading-tight"
              style={{ color: C.ink }}
            >
              {title}
            </h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded-md p-1.5 transition-colors hover:bg-neutral-100 focus:outline-none focus-visible:ring-2"
          >
            <Glyph name="close" size={16} color="#6B6580" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-5">{children}</div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  MAIN COMPONENT                                                     */
/* ------------------------------------------------------------------ */
export default function CASDMExplorer({
  defaultPhase = 0,
  onStepSelect,
  onGateSelect,
}) {
  const [phaseIdx, setPhaseIdx] = useState(defaultPhase);
  const [filters, setFilters] = useState(() => new Set(Object.keys(KIND)));
  const [query, setQuery] = useState("");
  const [openStep, setOpenStep] = useState(null);
  const [openGate, setOpenGate] = useState(null);
  const [openAgents, setOpenAgents] = useState(false);

  const phase = PHASES[phaseIdx];

  const toggleFilter = (k) =>
    setFilters((prev) => {
      const next = new Set(prev);
      next.has(k) ? next.delete(k) : next.add(k);
      return next.size === 0 ? new Set(Object.keys(KIND)) : next;
    });

  /* counts per kind for the current phase */
  const counts = useMemo(() => {
    const c = {};
    Object.keys(KIND).forEach((k) => (c[k] = 0));
    ROLES.forEach((r) =>
      (phase.lanes[r.id] || []).forEach((s) => (c[s.k] = (c[s.k] || 0) + 1))
    );
    return c;
  }, [phase]);

  const totalSteps = useMemo(
    () => Object.values(counts).reduce((a, b) => a + b, 0),
    [counts]
  );

  const q = query.trim().toLowerCase();
  const visible = (s) =>
    filters.has(s.k) &&
    (!q ||
      s.t.toLowerCase().includes(q) ||
      (s.a || "").toLowerCase().includes(q) ||
      (s.d || "").toLowerCase().includes(q));

  const handleStep = (s) => {
    setOpenStep(s);
    onStepSelect?.(s);
  };
  const handleGate = (p) => {
    setOpenGate(p);
    onGateSelect?.(p.gate);
  };

  const agentDetail = openStep?.a
    ? AGENTS.find((a) => a.id === openStep.a)
    : null;

  return (
    <div
      className="w-full"
      style={{ fontFamily: "ui-sans-serif, system-ui, sans-serif" }}
    >
      <div className="py-2">
        {/* ---------- Masthead ---------- */}
        <header className="mb-6">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <div
                className="mb-1 font-mono text-[11px] font-semibold tracking-[0.2em]"
                style={{ color: C.ember }}
              >
                CLOUDELLIGENT
              </div>
              <h1
                className="text-2xl font-semibold tracking-tight sm:text-3xl"
                style={{ color: C.ink }}
              >
                Agentic Software Delivery Model
              </h1>
              <p className="mt-1.5 max-w-2xl text-sm" style={{ color: "#6B6580" }}>
                Five phases, four roles, five gates. Every artifact is generated by an
                agent, validated by an agent, then reviewed by a human before it moves
                forward.
              </p>
            </div>
            <button
              type="button"
              onClick={() => setOpenAgents(true)}
              className="inline-flex items-center gap-2 rounded-lg px-3.5 py-2 text-sm font-medium text-white transition-opacity hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
              style={{ background: C.ink }}
            >
              <Glyph name="agent" size={15} color="#fff" />
              {AGENTS.length} agents
            </button>
          </div>
        </header>

        {/* ---------- Gate rail ---------- */}
        {/* `py-3` (not `p-2`) so the rotated gate diamonds — which overflow their own 36px boxes
            by ~2px at rest and ~4px at hover scale — have vertical room to paint now that the
            rail is `overflow-visible` rather than a scroll container. See GateRail. */}
        <div
          className="mb-6 rounded-xl border bg-white px-2 py-3 shadow-sm"
          style={{ borderColor: "#E6E2EE" }}
        >
          <GateRail
            phases={PHASES}
            active={phaseIdx}
            onPhase={setPhaseIdx}
            onGate={handleGate}
          />
          <p
            className="px-3 pb-1 pt-1 text-[11px]"
            style={{ color: "#9A93AD" }}
          >
            Diamonds are quality gates. Select one to see its exit criteria.
          </p>
        </div>

        {/* ---------- Phase header ---------- */}
        <div
          className="mb-5 rounded-xl border p-5 shadow-sm"
          style={{ background: C.ink, borderColor: C.ink }}
        >
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="max-w-3xl">
              <div
                className="mb-1.5 font-mono text-[11px] font-semibold tracking-widest"
                style={{ color: C.amber }}
              >
                PHASE {phase.num} · TRIGGERED BY {phase.trigger.toUpperCase()}
              </div>
              <h2 className="text-xl font-semibold text-white sm:text-2xl">
                {phase.name}
              </h2>
              <p
                className="mt-0.5 text-sm italic"
                style={{ color: C.amber }}
              >
                {phase.tagline}
              </p>
              <p className="mt-3 text-sm leading-relaxed text-white/75">
                {phase.summary}
              </p>
            </div>
            <button
              type="button"
              onClick={() => handleGate(phase)}
              className="inline-flex shrink-0 items-center gap-2 rounded-lg border px-3.5 py-2 text-sm font-medium transition-colors hover:bg-white/10 focus:outline-none focus-visible:ring-2"
              style={{ borderColor: "rgba(255,255,255,.3)", color: "#fff" }}
            >
              <Glyph name="gate" size={14} color={C.moss} />
              {phase.gate.id}
            </button>
          </div>
        </div>

        {/* ---------- Controls ---------- */}
        <div className="mb-3 flex flex-wrap items-center gap-2">
          {Object.keys(KIND).map((k) => (
            <KindChip
              key={k}
              kind={k}
              count={counts[k]}
              active={filters.has(k)}
              onClick={() => toggleFilter(k)}
            />
          ))}
          <div className="relative ml-auto">
            <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2">
              <Glyph name="search" size={14} color="#9A93AD" />
            </span>
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Find a step or agent"
              aria-label="Find a step or agent"
              className="w-56 rounded-full border bg-white py-1.5 pl-9 pr-3 text-xs outline-none transition-colors focus:border-current"
              style={{ borderColor: "#DDD9E6", color: C.ink }}
            />
          </div>
        </div>

        {/* ---------- Icon legend ---------- */}
        <div
          className="mb-4 flex flex-wrap items-center gap-x-5 gap-y-2 rounded-xl border bg-white/80 px-4 py-2.5"
          style={{ borderColor: "#E6E2EE" }}
        >
          <span className="text-[11px] font-semibold uppercase tracking-wide" style={{ color: "#9A93AD" }}>
            Legend
          </span>
          {Object.entries(KIND).map(([key, def]) => (
            <span key={key} className="inline-flex items-center gap-1.5">
              <span
                className="flex h-5 w-5 items-center justify-center rounded-md"
                style={{ background: `${def.color}1A` }}
              >
                <Glyph name={def.glyph} size={12} color={def.color} />
              </span>
              <span className="text-[12px]" style={{ color: C.inkSoft }}>
                {def.label}
              </span>
            </span>
          ))}
          <span className="ml-auto inline-flex items-center gap-1.5">
            <span
              className="flex h-5 w-5 rotate-45 items-center justify-center rounded-[4px] border-2 bg-white"
              style={{ borderColor: C.moss }}
            >
              <span className="-rotate-45 font-mono text-[9px] font-bold" style={{ color: C.moss }}>
                ◆
              </span>
            </span>
            <span className="text-[12px]" style={{ color: C.inkSoft }}>
              Quality gate
            </span>
          </span>
        </div>

        {/* ---------- Swimlanes ---------- */}
        <div className="grid gap-4 lg:grid-cols-2 xl:grid-cols-4">
          {ROLES.map((role) => {
            const steps = (phase.lanes[role.id] || []).filter(visible);
            return (
              <section
                key={role.id}
                className="rounded-xl border bg-white/70 p-3"
                style={{ borderColor: "#E6E2EE" }}
              >
                <header className="mb-3 flex items-center gap-2 px-1">
                  <span
                    className="flex h-6 w-6 items-center justify-center rounded-md font-mono text-[10px] font-bold text-white"
                    style={{ background: C.ink }}
                  >
                    {role.short}
                  </span>
                  <h3
                    className="text-xs font-semibold uppercase tracking-wide"
                    style={{ color: C.inkSoft }}
                  >
                    {role.name}
                  </h3>
                  <span
                    className="ml-auto font-mono text-[11px] tabular-nums"
                    style={{ color: "#9A93AD" }}
                  >
                    {steps.length}
                  </span>
                </header>
                <div className="space-y-2">
                  {steps.length === 0 ? (
                    <p
                      className="rounded-lg border border-dashed px-3 py-6 text-center text-xs"
                      style={{ borderColor: "#E0DBEC", color: "#9A93AD" }}
                    >
                      {(phase.lanes[role.id] || []).length === 0
                        ? "Not active in this phase"
                        : "Nothing matches the current filters"}
                    </p>
                  ) : (
                    steps.map((s, i) => (
                      <StepCard key={i} step={s} onOpen={handleStep} />
                    ))
                  )}
                </div>
              </section>
            );
          })}
        </div>

        {/* ---------- Footer ---------- */}
        <div
          className="mt-6 flex flex-wrap items-center justify-between gap-3 border-t pt-4 text-xs"
          style={{ borderColor: "#E0DBEC", color: "#9A93AD" }}
        >
          <span>
            {totalSteps} steps in Phase {phase.num} · Governance and reporting run
            across every phase
          </span>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => setPhaseIdx((i) => Math.max(0, i - 1))}
              disabled={phaseIdx === 0}
              className="rounded-md px-2 py-1 font-medium transition-colors enabled:hover:bg-white disabled:opacity-40"
              style={{ color: C.ink }}
            >
              ← Previous phase
            </button>
            <button
              type="button"
              onClick={() =>
                setPhaseIdx((i) => Math.min(PHASES.length - 1, i + 1))
              }
              disabled={phaseIdx === PHASES.length - 1}
              className="rounded-md px-2 py-1 font-medium transition-colors enabled:hover:bg-white disabled:opacity-40"
              style={{ color: C.ink }}
            >
              Next phase →
            </button>
          </div>
        </div>
      </div>

      {/* ---------- Step panel ---------- */}
      <Panel
        open={!!openStep}
        onClose={() => setOpenStep(null)}
        eyebrow={openStep ? KIND[openStep.k].label.toUpperCase() : ""}
        title={openStep?.t || ""}
        accent={openStep ? KIND[openStep.k].color : C.ink}
      >
        {openStep && (
          <div className="space-y-5">
            {openStep.d && (
              <p className="text-sm leading-relaxed" style={{ color: C.inkSoft }}>
                {openStep.d}
              </p>
            )}
            {agentDetail && (
              <div
                className="rounded-lg border p-4"
                style={{ borderColor: "#E6E2EE", background: `${C.sky}0A` }}
              >
                <div
                  className="mb-1 font-mono text-sm font-semibold"
                  style={{ color: C.sky }}
                >
                  {agentDetail.name}
                </div>
                <p className="text-sm" style={{ color: C.inkSoft }}>
                  {agentDetail.role}
                </p>
                <div
                  className="mt-3 mb-1.5 text-[11px] font-semibold uppercase tracking-wide"
                  style={{ color: "#9A93AD" }}
                >
                  Produces
                </div>
                <ul className="space-y-1">
                  {agentDetail.outputs.map((o) => (
                    <li
                      key={o}
                      className="flex gap-2 text-sm"
                      style={{ color: C.inkSoft }}
                    >
                      <span style={{ color: C.ember }}>·</span>
                      {o}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <div
              className="rounded-lg p-4 text-sm"
              style={{ background: C.mist, color: C.inkSoft }}
            >
              This step sits in{" "}
              <strong style={{ color: C.ink }}>
                Phase {phase.num} · {phase.name}
              </strong>
              . It must clear{" "}
              <strong style={{ color: C.ink }}>{phase.gate.id}</strong> before the
              project moves on.
            </div>
          </div>
        )}
      </Panel>

      {/* ---------- Gate panel ---------- */}
      <Panel
        open={!!openGate}
        onClose={() => setOpenGate(null)}
        eyebrow={openGate ? `${openGate.gate.id} · EXIT CRITERIA` : ""}
        title={openGate?.gate.title || ""}
        accent={C.moss}
      >
        {openGate && (
          <div className="space-y-5">
            <p className="text-sm leading-relaxed" style={{ color: C.inkSoft }}>
              Every item has to be true before Phase {openGate.num} closes. If any
              one fails, the phase does not advance.
            </p>
            <ul className="space-y-2">
              {openGate.gate.criteria.map((c) => (
                <li
                  key={c}
                  className="flex items-start gap-2.5 rounded-lg border bg-white p-3"
                  style={{ borderColor: "#E6E2EE" }}
                >
                  <span
                    className="mt-px flex h-5 w-5 shrink-0 items-center justify-center rounded-full"
                    style={{ background: `${C.moss}1F` }}
                  >
                    <Glyph name="task" size={12} color={C.moss} />
                  </span>
                  <span className="text-sm" style={{ color: C.ink }}>
                    {c}
                  </span>
                </li>
              ))}
            </ul>
            <div
              className="rounded-lg border-l-4 p-4"
              style={{ borderColor: C.ember, background: `${C.ember}0D` }}
            >
              <div
                className="mb-1 text-[11px] font-semibold uppercase tracking-wide"
                style={{ color: C.ember }}
              >
                If the gate fails
              </div>
              <p className="text-sm" style={{ color: C.inkSoft }}>
                {openGate.gate.onFail}
              </p>
            </div>
          </div>
        )}
      </Panel>

      {/* ---------- Agent roster panel ---------- */}
      <Panel
        open={openAgents}
        onClose={() => setOpenAgents(false)}
        eyebrow="AGENT ROSTER"
        title="Who does what"
        accent={C.sky}
      >
        <div className="space-y-3">
          {AGENTS.map((a) => (
            <div
              key={a.id}
              className="rounded-lg border bg-white p-4"
              style={{ borderColor: "#E6E2EE" }}
            >
              <div className="flex items-start justify-between gap-3">
                <div
                  className="font-mono text-sm font-semibold"
                  style={{ color: C.sky }}
                >
                  {a.name}
                </div>
                <div className="flex shrink-0 gap-1">
                  {a.phases.map((p) => (
                    <button
                      key={p}
                      type="button"
                      onClick={() => {
                        setPhaseIdx(p);
                        setOpenAgents(false);
                      }}
                      title={`Go to Phase ${p}`}
                      className="flex h-5 w-5 items-center justify-center rounded font-mono text-[10px] font-bold transition-colors hover:opacity-80"
                      style={{ background: C.mist, color: C.inkSoft }}
                    >
                      {p}
                    </button>
                  ))}
                </div>
              </div>
              <p className="mt-1 text-sm" style={{ color: C.inkSoft }}>
                {a.role}
              </p>
              <div className="mt-2.5 flex flex-wrap gap-1.5">
                {a.outputs.map((o) => (
                  <span
                    key={o}
                    className="rounded px-2 py-0.5 text-[11px]"
                    style={{ background: `${C.ember}12`, color: C.ember }}
                  >
                    {o}
                  </span>
                ))}
              </div>
            </div>
          ))}
          <p className="pt-1 text-xs" style={{ color: "#9A93AD" }}>
            executioner* is marked in the source model — treat the name as
            provisional. Numbers next to each agent are the phases it runs in;
            select one to jump there.
          </p>
        </div>
      </Panel>
    </div>
  );
}
